import {
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Fab,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useCollectedInformation } from "../hooks/collectedinformation.hooks";
import { InformationDetailModel } from "../../models/information.model";
import CreateCollectableInformation from "./CreateCollectableInformation";
import { SaveAlt } from "@mui/icons-material";
import ViewCollectableInformation from "./ViewCollectableInformation";
import { deleteAgent } from "../../services/agent.service";
import { useDisclosure } from "@mantine/hooks";
import { deleteCollectedInformation } from "../../services/collectedinformation.service";

function ListCollectedInformation() {
  const [collectableInformation, setCollectableInformation] = useState<
    InformationDetailModel[] | null
  >(null);
  const { data, isLoading } = useCollectedInformation();
  const [informationIdToDelete, setInformationIdToDelete] = useState<
    string | null | undefined
  >(null);
  const [viewCollectableInformation, setViewCollectableInformation] = useState<
    InformationDetailModel | null | undefined
  >(null);

  useEffect(() => {
    if (data) {
      setCollectableInformation(data);
    }
  }, [data]);

  const onNewInformationCreated = (newInfo: InformationDetailModel) => {
    if (newInfo) {
      setCollectableInformation((prevInfo: InformationDetailModel[] | null) => {
        return [...(prevInfo ?? []), newInfo];
      });
    }
  };

  const onDeleteInformation = async () => {
    try {
      if (informationIdToDelete) {
        if (
          window.confirm(
            "If you delete this data, it will be removed from all agents using it. You can't undo this action."
          )
        ) {
          await deleteCollectedInformation(informationIdToDelete);
          setCollectableInformation(
            (prevInfo) =>
              prevInfo?.filter((info) => info.id !== informationIdToDelete) ??
              []
          );
          alert("Collectable data deleted successfully.");
        }
      }
    } catch (error) {
      alert("Failed to delete agent. Please try again.");
      console.error("Error deleting agent:", error);
    } finally {
      setInformationIdToDelete(null);
    }
  };

  useEffect(() => {
    if (informationIdToDelete) {
      onDeleteInformation();
    }
  }, [informationIdToDelete]);

  return (
    <Stack
      sx={{
        width: "100%",
        alignItems: "flex-start",
        flexWrap: "wrap",
        justifyContent: "flex-start",
        rowGap: 3,
      }}
    >
      <Stack
        sx={{ width: "100%" }}
        alignItems={"flex-start"}
        justifyContent={"flex-start"}
      >
        <Typography
          variant="h6"
          sx={{ color: "text.secondary", textAlign: "left" }}
        >
          Collectable Data
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", fontSize: 14, textAlign: "left" }}
        >
          View all the <b>collectable data</b> your agents can collect from
          users, like name, email, and phone number. Easily track and manage the
          data to generate leads and grow your sales.
        </Typography>
      </Stack>

      {isLoading ? (
        <Stack
          sx={{ width: "100%" }}
          justifyContent={"center"}
          alignItems={"center"}
        >
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={3}>
          {collectableInformation?.map(
            (info: InformationDetailModel, index) => (
              <Grid
                key={info.id}
                size={{ xs: 12, sm: 6, md: 4 }}
                sx={{
                  xs: {
                    width: "100%",
                  },
                  md: {
                    width: 200,
                  },
                }}
              >
                <Card
                  sx={{
                    xs: {
                      width: "100%",
                    },
                    md: {
                      width: 275,
                    },
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    p: 0,

                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "none",
                      boxShadow: "none",
                    },
                  }}
                >
                  <CardContent
                    sx={{ flexGrow: 1 }}
                    onClick={() => setViewCollectableInformation(info)}
                  >
                    <Grid container spacing={2}>
                      <Grid
                        size={{
                          xs: 2,
                          md: 3,
                        }}
                      >
                        <Stack
                          sx={{ flexGrow: 1, height: "100%", width: "100%" }}
                          alignItems={"center"}
                          justifyContent={"center"}
                        >
                          <IconButton
                            sx={{
                              background: "rgba(25, 118, 210, 0.1) !important",
                              borderRadius: "50% !important",
                            }}
                            disabled
                          >
                            <SaveAlt />
                          </IconButton>
                        </Stack>
                      </Grid>
                      <Grid
                        size={{
                          xs: 10,
                          md: 9,
                        }}
                      >
                        <Stack spacing={1} alignItems={"flex-start"}>
                          <Typography
                            variant="h6"
                            sx={{
                              textAlign: "left",
                              fontSize: 14,
                              fontWeight: 500,
                            }}
                          >
                            {info.propertyLabel}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "text.secondary",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              textAlign: "left",
                            }}
                          >
                            {info.propertyDescription ??
                              "No description available."}
                          </Typography>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>

                  <CardActions
                    sx={{
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack
                      justifyContent={"space-between"}
                      alignItems={"stretch"}
                      direction={"row"}
                      sx={{ width: "100%" }}
                    >
                      <Button
                        size="small"
                        variant="text"
                        disableElevation
                        color="error"
                        onClick={() => {
                          setInformationIdToDelete(info.id);
                        }}
                        loading={informationIdToDelete === info.id}
                        disabled={informationIdToDelete === info.id}
                      >
                        Delete
                      </Button>
                      <Stack
                        direction="row"
                        spacing={1}
                        flex={1}
                        flexGrow={1}
                        justifyContent={"flex-end"}
                        sx={{ width: "100%" }}
                      >
                        <Button
                          size="small"
                          variant="text"
                          disableElevation
                          onClick={() => setViewCollectableInformation(info)}
                          disabled={isLoading}
                        >
                          View
                        </Button>
                      </Stack>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            )
          )}
        </Grid>
      )}

      <ViewCollectableInformation
        informationToShow={viewCollectableInformation}
        onClose={() => setViewCollectableInformation(null)}
      />
      <CreateCollectableInformation onSaveSuccess={onNewInformationCreated} />
    </Stack>
  );
}

export default ListCollectedInformation;
